<template>
    <div class="h-8 w-8 grid place-items-center overflow-hidden" :class="[backgroundColorClass, textColorClass, sizeClasses]" :style="{'height': computedSize, 'width': computedSize}">
      <div v-if="imgSrc" class="d-avatar--image h-full w-full">
        <div class="bg-cover bg-center h-full rounded-full" style="padding-bottom:100%" :style="{'background-image': `url(${imgSrc})`}" />
      </div>
      <span v-else-if="text" class="font-medium" :style="{'font-size': computedFontSize}">
        {{ initials }}
      </span>
      <span v-else>
        <d-icon icon="user" color="current" :style="{'font-size': computedFontIconSize}" />
      </span>
    </div>
  </template>
  
  <script>
    export default {
      props: {
        /**
         * Background color, if text is showing. Use a pre-defined color (`primary-500`, etc)
         */
        color: {
          type: String,
          default: 'text-primary',
        },
  
        /**
         * Up to two initials will be pulled from the text
         */
        text: {
          type: String,
          default: '',
        },
  
        /**
         * Image to show. This will take priority over text.
         */
        imgSrc: {
          type: String,
          default: '',
        },
  
        /**
         * Image/Circle Size. Include px/em/rem. Defaults to px if a number.
         */
        size: {
          type: [String, Number],
          default: '32px',
          validator: (val) => Number.parseInt(val, 10) || ((val.indexOf('px') > -1 || val.indexOf('em') > -1 || val.indexOf('rem')) > -1),
        },
  
        /*
         * Removes the border-radius from the component
         */
        tile: {
          type: Boolean,
          default: false,
        },
      },
  
      computed: {
        backgroundColorClass() {
          if (this.imgSrc) return ''; // No background color needed for images
          
          // Map color names to Tailwind CSS classes
          const colorMap = {
            'orange': 'bg-orange-500',
            'blue': 'bg-blue-500',
            'green': 'bg-green-500',
            'red': 'bg-red-500',
            'purple': 'bg-purple-500',
            'pink': 'bg-pink-500',
            'yellow': 'bg-yellow-500',
            'indigo': 'bg-indigo-500',
            'teal': 'bg-teal-500',
            'gray': 'bg-gray-500',
            'primary': 'bg-primary',
            'secondary': 'bg-secondary',
            'accent': 'bg-accent',
          };
          
          return colorMap[this.color] || 'bg-primary';
        },
        
        textColorClass() {
          if (this.imgSrc) return ''; // No text color needed for images
          
          // Map color names to appropriate text colors for contrast
          const textColorMap = {
            'orange': 'text-white',
            'blue': 'text-white',
            'green': 'text-white',
            'red': 'text-white',
            'purple': 'text-white',
            'pink': 'text-white',
            'yellow': 'text-black',
            'indigo': 'text-white',
            'teal': 'text-white',
            'gray': 'text-white',
            'primary': 'text-primary-content',
            'secondary': 'text-secondary-content',
            'accent': 'text-accent-content',
          };
          
          return textColorMap[this.color] || 'text-primary-content';
        },
        
        sizeClasses() {
          return {
            'rounded-full': !this.tile,
          };
        },
  
        initials() {
          const words = this.text.split(' ');
          let initials = words[0].substring(0, 1).toUpperCase();
  
          if (words.length > 1) {
            initials += words[words.length - 1].substring(0, 1).toUpperCase();
          }
          return initials;
        },
  
        computedSize() {
          return this.convertToUnit(this.size);
        },
  
        computedFontSize() {
          const adjustedSize = this.size;
          if (!Number.parseInt(adjustedSize, 10)) return '14px';
          return `${Number.parseInt(adjustedSize, 10) / 2.25}px`;
        },
  
        computedFontIconSize() {
          const adjustedSize = this.size;
          if (!Number.parseInt(adjustedSize, 10)) return '14px';
          return `${Number.parseInt(adjustedSize, 10) / 2.75}px`;
        },
      },
  
      methods: {
        convertToUnit(str, unit = 'px') {
          if (str == null || str === '') {
            return undefined;
          } if (Number.isNaN(+str)) {
            return String(str);
          }
          return `${Number(str)}${unit}`;
        },
      },
    };
  </script>